import type { ResolvedFigure } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { clamp, fits, type Frame, frameFor, header, note, textWidth, TYPE } from './kit.js';

export interface RangeBand {
  id: string;
  label: string;
  from: number;
  to: number;
}

export interface RangeMark {
  id: string;
  label: string;
  /** A point when `to` is omitted; an interval when it is present. */
  from: number;
  to?: number;
  /** One factual clause: what the position or the width means. */
  detail?: string;
}

export interface RangeCompanionEntry {
  at: number;
  text: string;
}

export interface RangeNumberLineSpec extends PrimitiveSpec {
  family: 'range-number-line';
  view: 'orthographic';
  subject: string;
  /** What the axis measures, e.g. 'pH'. */
  quantity: string;
  domain: { min: number; max: number };
  tickStep: number;
  /** Classification zones covering the domain. */
  bands: RangeBand[];
  /** A single boundary value the bands meet at, if the domain has one. */
  threshold?: { value: number; label: string };
  marks: RangeMark[];
  /** A second scale on the same axis: what one step of the quantity really means. */
  companion?: { label: string; entries: RangeCompanionEntry[] };
  focusMarkId?: string;
  title: string;
  subtitle: string;
}

export const rangeBandId = (id: string): string => `range.band.${id}`;
export const rangeMarkId = (id: string): string => `range.mark.${id}`;
export const RANGE_AXIS_ID = 'range.axis';
export const RANGE_THRESHOLD_ID = 'range.threshold';
export const RANGE_COMPANION_ID = 'range.companion';

interface Metrics {
  frame: Frame;
  axisX: number;
  axisW: number;
  bandsY: number;
  bandsH: number;
  axisY: number;
  tickLabelY: number;
  companionY: number;
  marksTop: number;
  markRow: number;
  labelBeside: boolean;
  footerY: number;
  required: number;
}

function metrics(spec: RangeNumberLineSpec): Metrics {
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const axisX = frame.margin + 8;
  const axisW = frame.contentWidth - 16;
  const bandsY = frame.top + 8;
  const bandsH = 26;
  const axisY = bandsY + bandsH;
  const tickLabelY = axisY + 20;
  const companionY = spec.companion ? tickLabelY + 30 : tickLabelY;
  const marksTop = companionY + (spec.companion ? 34 : 18);
  // A label may sit beside its mark only when *every* label has room on one
  // side. Otherwise the whole figure stacks labels under their marks: a density
  // decision taken once, never a per-label special case.
  const span = spec.domain.max - spec.domain.min;
  const at = (value: number): number => axisX + ((value - spec.domain.min) / span) * axisW;
  const labelBeside = spec.marks.every((mark) => {
    const x1 = at(mark.from);
    const x2 = mark.to === undefined ? x1 : at(mark.to);
    const extent = mark.to === undefined ? String(mark.from) : `${mark.from}–${mark.to}`;
    const width = Math.max(
      textWidth(`${mark.label} ${extent}`, TYPE.label, true),
      mark.detail ? textWidth(mark.detail, TYPE.micro, true) : 0,
    );
    return Math.max(x1 - axisX - 12, axisX + axisW - x2 - 12) >= width;
  });
  const markRow = labelBeside ? 46 : 58;
  const footerY = marksTop + spec.marks.length * markRow + 20;
  return { frame, axisX, axisW, bandsY, bandsH, axisY, tickLabelY, companionY, marksTop, markRow, labelBeside, footerY, required: footerY + 14 };
}

/**
 * A number line is the only figure where position *is* the claim.
 *
 * Anything can be a row in a table; a range says that a quantity is continuous,
 * that a value sits at exactly one place on it, and that a boundary decides
 * which side of a distinction a thing falls on. So intervals here are drawn to
 * scale with their real width — a narrow band must look narrow — and the
 * companion scale states what one step of the axis actually costs, which is the
 * fact a table can never carry.
 */
export function compileRangeNumberLine(spec: RangeNumberLineSpec): ResolvedFigure {
  validateRangeNumberLine(spec);

  const builder = new SceneBuilder();
  const m = metrics(spec);
  const { frame } = m;
  const warnings: string[] = [];
  const span = spec.domain.max - spec.domain.min;
  const at = (value: number): number => m.axisX + ((value - spec.domain.min) / span) * m.axisW;

  header(builder, frame, 'range', `SCALE · ${spec.subject.toUpperCase()}`, [
    `${spec.quantity.toUpperCase()} ${format(spec.domain.min)} TO ${format(spec.domain.max)} · POSITION IS THE CLAIM`,
    `${spec.quantity.toUpperCase()} ${format(spec.domain.min)} TO ${format(spec.domain.max)}`,
  ]);

  for (const band of spec.bands) {
    const semanticId = rangeBandId(band.id);
    const x1 = at(band.from);
    const x2 = at(band.to);
    builder.rect(`${semanticId}.zone`, semanticId, 'surface', box(x1, m.bandsY, x2 - x1, m.bandsH), {
      label: `${band.label}: ${format(band.from)} to ${format(band.to)}`,
      interactive: true,
    });
    const text = band.label.toUpperCase();
    if (fits(text, TYPE.micro, x2 - x1 - 12, true, true)) {
      builder.text(`${semanticId}.label`, semanticId, 'annotation', point((x1 + x2) / 2, m.bandsY + 17), text, {
        size: TYPE.micro, mono: true, weight: 700, align: 'middle', uppercase: true,
      });
    } else {
      warnings.push(`Zone ${band.label} is too narrow to label at this width.`);
    }
    builder.anchor(`${semanticId}.start`, semanticId, point(x1, m.bandsY), 'left');
    builder.hit(semanticId, box(x1, m.bandsY, x2 - x1, m.bandsH));
    builder.relation(`${semanticId}.covers`, 'classifies', semanticId, RANGE_AXIS_ID, `${band.label} covers ${format(band.from)} to ${format(band.to)} ${spec.quantity}`);
  }

  builder.line(`${RANGE_AXIS_ID}.line`, RANGE_AXIS_ID, 'axis', point(m.axisX, m.axisY), point(m.axisX + m.axisW, m.axisY));
  const tickCount = Math.round(span / spec.tickStep);
  const labelEvery = Math.max(1, Math.ceil((tickCount + 1) / Math.floor(m.axisW / 34)));
  for (let tick = 0; tick <= tickCount; tick += 1) {
    const value = spec.domain.min + tick * spec.tickStep;
    const x = at(value);
    const major = tick % labelEvery === 0;
    builder.line(`${RANGE_AXIS_ID}.tick.${tick}`, RANGE_AXIS_ID, 'axis', point(x, m.axisY), point(x, m.axisY + (major ? 7 : 4)));
    if (major) {
      builder.text(`${RANGE_AXIS_ID}.tick.${tick}.label`, RANGE_AXIS_ID, 'annotation', point(x, m.tickLabelY), format(value), {
        size: TYPE.micro,
        mono: true,
        weight: 500,
        align: tick === 0 ? 'start' : tick === tickCount ? 'end' : 'middle',
      });
    }
  }
  builder.anchor(`${RANGE_AXIS_ID}.origin`, RANGE_AXIS_ID, point(m.axisX, m.axisY), 'left');
  builder.anchor(`${RANGE_AXIS_ID}.end`, RANGE_AXIS_ID, point(m.axisX + m.axisW, m.axisY), 'right');
  builder.hit(RANGE_AXIS_ID, box(m.axisX, m.axisY - 6, m.axisW, 22));

  if (spec.threshold) {
    const x = at(spec.threshold.value);
    builder.line(`${RANGE_THRESHOLD_ID}.line`, RANGE_THRESHOLD_ID, 'dimension', point(x, m.bandsY - 12), point(x, m.axisY + 8), { dash: '5 4' });
    const text = `${spec.threshold.label.toUpperCase()} ${format(spec.threshold.value)}`;
    builder.text(`${RANGE_THRESHOLD_ID}.label`, RANGE_THRESHOLD_ID, 'annotation', point(clamp(x, m.axisX + textWidth(text, TYPE.micro, true, true) / 2, m.axisX + m.axisW - textWidth(text, TYPE.micro, true, true) / 2), m.bandsY - 16), text, {
      size: TYPE.micro, mono: true, weight: 700, align: 'middle', uppercase: true,
    });
    builder.anchor(`${RANGE_THRESHOLD_ID}.axis`, RANGE_THRESHOLD_ID, point(x, m.axisY), 'down');
    builder.hit(RANGE_THRESHOLD_ID, box(x - 12, m.bandsY - 22, 24, m.axisY - m.bandsY + 30));
    builder.relation(`${RANGE_THRESHOLD_ID}.divides`, 'divides', RANGE_THRESHOLD_ID, RANGE_AXIS_ID, `${spec.threshold.label} at ${format(spec.threshold.value)} ${spec.quantity}`);
  }

  if (spec.companion) {
    builder.text(`${RANGE_COMPANION_ID}.label`, RANGE_COMPANION_ID, 'annotation', point(m.axisX, m.companionY - 12), spec.companion.label.toUpperCase(), {
      size: TYPE.micro, mono: true, weight: 700, uppercase: true,
    });
    builder.line(`${RANGE_COMPANION_ID}.line`, RANGE_COMPANION_ID, 'grid', point(m.axisX, m.companionY), point(m.axisX + m.axisW, m.companionY));
    // Keep every companion label at full size: thin the ruler instead, and drop
    // the final entry rather than let it collide with the one before it.
    const entries = spec.companion.entries;
    const step = Math.max(1, Math.ceil(entries.length / Math.max(2, Math.floor(m.axisW / 48))));
    const keptIndices = entries.map((_, index) => index).filter((index) => index % step === 0);
    const lastIndex = entries.length - 1;
    if (!keptIndices.includes(lastIndex)) {
      const previous = keptIndices[keptIndices.length - 1]!;
      if (at(entries[lastIndex]!.at) - at(entries[previous]!.at) >= 58) keptIndices.push(lastIndex);
    }
    entries.forEach((entry, index) => {
      if (!keptIndices.includes(index)) return;
      const x = at(entry.at);
      builder.line(`${RANGE_COMPANION_ID}.tick.${index}`, RANGE_COMPANION_ID, 'grid', point(x, m.companionY - 4), point(x, m.companionY + 4));
      builder.text(`${RANGE_COMPANION_ID}.entry.${index}`, RANGE_COMPANION_ID, 'annotation', point(x, m.companionY + 16), entry.text, {
        size: TYPE.micro,
        mono: true,
        weight: 500,
        align: index === 0 ? 'start' : index === lastIndex ? 'end' : 'middle',
      });
    });
    builder.anchor(`${RANGE_COMPANION_ID}.origin`, RANGE_COMPANION_ID, point(m.axisX, m.companionY), 'left');
    builder.hit(RANGE_COMPANION_ID, box(m.axisX, m.companionY - 14, m.axisW, 34));
    builder.relation(`${RANGE_COMPANION_ID}.restates`, 'restates', RANGE_COMPANION_ID, RANGE_AXIS_ID, `${spec.companion.label} on the same axis`);
  }

  // Each mark gets its own lane, drawn at its true position and true width. The
  // label never sits on top of the mark it names: it takes the side with room,
  // so no connector has to cross another mark to reach its own text.
  spec.marks.forEach((mark, index) => {
    const semanticId = rangeMarkId(mark.id);
    const laneTop = m.marksTop + index * m.markRow;
    const y = laneTop + 9;
    const x1 = at(mark.from);
    const x2 = mark.to === undefined ? x1 : at(mark.to);
    const active = mark.id === spec.focusMarkId;
    const width = x2 - x1;
    const centre = (x1 + x2) / 2;

    // The lane rule runs in from the edge to the mark on the side the label does
    // not take, so nothing is ever struck through by its own guide.
    const labelOnTheRight = centre < m.axisX + m.axisW / 2;
    if (!m.labelBeside) {
      builder.line(`${semanticId}.lane`, semanticId, 'grid', point(m.axisX, y), point(m.axisX + m.axisW, y), { dash: '2 6' });
    } else if (labelOnTheRight) {
      builder.line(`${semanticId}.lane`, semanticId, 'grid', point(m.axisX, y), point(Math.max(m.axisX, x1 - 8), y), { dash: '2 6' });
    } else {
      builder.line(`${semanticId}.lane`, semanticId, 'grid', point(Math.min(m.axisX + m.axisW, x2 + 8), y), point(m.axisX + m.axisW, y), { dash: '2 6' });
    }
    if (mark.to === undefined) {
      builder.rect(`${semanticId}.pin`, semanticId, active ? 'active' : 'positive', box(x1 - 5, y - 5, 10, 10), { radius: 5, label: mark.label });
    } else {
      builder.rect(`${semanticId}.span`, semanticId, active ? 'active' : 'positive', box(x1, y - 8, Math.max(width, 2), 16), {
        radius: 2,
        label: `${mark.label}: ${format(mark.from)} to ${format(mark.to)}`,
        interactive: true,
      });
      builder.line(`${semanticId}.cap.start`, semanticId, active ? 'active' : 'dimension', point(x1, y - 13), point(x1, y + 13));
      builder.line(`${semanticId}.cap.end`, semanticId, active ? 'active' : 'dimension', point(x2, y - 13), point(x2, y + 13));
      if (width < 6) warnings.push(`${mark.label} spans ${format((mark.to ?? 0) - mark.from)} ${spec.quantity} — a few pixels wide here, and drawn that way.`);
    }

    const extent = mark.to === undefined ? format(mark.from) : `${format(mark.from)}–${format(mark.to)}`;
    const labelText = `${mark.label} ${extent}`;
    const labelWidth = Math.max(textWidth(labelText, TYPE.label, true), mark.detail ? textWidth(mark.detail, TYPE.micro, true) : 0);
    const labelAnchor = !m.labelBeside
      ? m.axisX
      : labelOnTheRight
        ? clamp(x2 + 12, m.axisX, m.axisX + m.axisW - labelWidth)
        : clamp(x1 - 12, m.axisX + labelWidth, m.axisX + m.axisW);
    const align = m.labelBeside && !labelOnTheRight ? 'end' : 'start';
    const labelBaseline = m.labelBeside ? y + 4 : y + 24;
    builder.text(`${semanticId}.label`, semanticId, 'structure', point(labelAnchor, labelBaseline), labelText, {
      size: TYPE.label, mono: true, weight: 700, align,
    });
    if (mark.detail) {
      builder.text(`${semanticId}.detail`, semanticId, 'annotation', point(labelAnchor, labelBaseline + 15), mark.detail, {
        size: TYPE.micro, mono: true, weight: 500, align,
      });
    }
    // Only the taught mark is traced back to the axis, and only through the gaps
    // between lanes, so the guide never crosses another mark's label.
    if (active) {
      builder.line(`${semanticId}.guide.axis`, semanticId, 'construction', point(centre, m.axisY + 4), point(centre, m.marksTop - 4), { dash: '3 5' });
      for (let above = 0; above < index; above += 1) {
        const gapTop = m.marksTop + above * m.markRow + (m.labelBeside ? 30 : 44);
        const gapBottom = m.marksTop + (above + 1) * m.markRow - 6;
        builder.line(`${semanticId}.guide.gap.${above}`, semanticId, 'construction', point(centre, gapTop), point(centre, gapBottom), { dash: '3 5' });
      }
    }
    builder.anchor(`${semanticId}.start`, semanticId, point(x1, y), 'left');
    builder.anchor(`${semanticId}.end`, semanticId, point(x2, y), 'right');
    builder.hit(semanticId, box(m.axisX, laneTop - 4, m.axisW, m.markRow - 6));
    builder.relation(`${semanticId}.sits`, mark.to === undefined ? 'sits-at' : 'spans', semanticId, RANGE_AXIS_ID, `${mark.label} ${mark.to === undefined ? 'sits at' : 'spans'} ${extent} ${spec.quantity}`);
    for (const band of spec.bands) {
      if (mark.from >= band.from && (mark.to ?? mark.from) <= band.to) {
        builder.relation(`${semanticId}.within.${band.id}`, 'falls-in', semanticId, rangeBandId(band.id), `${mark.label} falls entirely in ${band.label}`);
      }
    }
  });

  const notes = [
    `${spec.marks.length} MEASURED VALUES · WIDTH IS THE REAL SPREAD`,
    `${spec.marks.length} VALUES · WIDTH IS THE REAL SPREAD`,
    `${spec.marks.length} MEASURED VALUES`,
  ];
  note(builder, 'range.note', 'range', point(frame.margin, m.footerY), notes.find((candidate) => fits(candidate, TYPE.micro, frame.contentWidth, true, true)) ?? notes[notes.length - 1]!);

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: spec.focusMarkId ? `focus:${spec.focusMarkId}` : 'overview',
    active: [spec.focusMarkId ? rangeMarkId(spec.focusMarkId) : RANGE_AXIS_ID, ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateRangeNumberLine(spec: RangeNumberLineSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'range-number-line' || spec.view !== 'orthographic') throw new Error('range-number-line: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('range-number-line: provenance is required');
  if (!spec.subject.trim() || !spec.quantity.trim() || !spec.title.trim() || !spec.subtitle.trim()) throw new Error('range-number-line: subject, quantity, title and subtitle are required');
  if (!Number.isFinite(spec.domain.min) || !Number.isFinite(spec.domain.max) || spec.domain.min >= spec.domain.max) throw new Error('range-number-line: the domain must run from a smaller number to a larger one');
  if (!Number.isFinite(spec.tickStep) || spec.tickStep <= 0 || spec.tickStep > spec.domain.max - spec.domain.min) throw new Error('range-number-line: invalid tick step');
  if (spec.bands.length < 1 || spec.bands.length > 4) throw new Error('range-number-line: require 1…4 zones');
  const bandIds = new Set<string>();
  let covered = spec.domain.min;
  for (const band of spec.bands) {
    if (!/^[a-z][a-z0-9-]*$/.test(band.id)) throw new Error(`range-number-line: invalid zone id ${band.id}`);
    if (bandIds.has(band.id)) throw new Error(`range-number-line: duplicate zone id ${band.id}`);
    bandIds.add(band.id);
    if (!band.label.trim()) throw new Error(`range-number-line: zone ${band.id} needs a label`);
    if (band.from !== covered) throw new Error('range-number-line: zones must tile the domain without gaps or overlap');
    if (band.to <= band.from || band.to > spec.domain.max) throw new Error(`range-number-line: zone ${band.id} leaves the domain`);
    covered = band.to;
  }
  if (covered !== spec.domain.max) throw new Error('range-number-line: zones must cover the whole domain');
  if (spec.marks.length < 1 || spec.marks.length > 5) throw new Error('range-number-line: require 1…5 marks');
  const markIds = new Set<string>();
  for (const mark of spec.marks) {
    if (!/^[a-z][a-z0-9-]*$/.test(mark.id)) throw new Error(`range-number-line: invalid mark id ${mark.id}`);
    if (markIds.has(mark.id)) throw new Error(`range-number-line: duplicate mark id ${mark.id}`);
    markIds.add(mark.id);
    if (!mark.label.trim() || mark.label.length > 20) throw new Error(`range-number-line: mark ${mark.id} needs a label of 1…20 characters`);
    if (!Number.isFinite(mark.from) || mark.from < spec.domain.min || mark.from > spec.domain.max) throw new Error(`range-number-line: mark ${mark.id} lies outside the domain`);
    if (mark.to !== undefined) {
      if (!Number.isFinite(mark.to) || mark.to <= mark.from || mark.to > spec.domain.max) throw new Error(`range-number-line: mark ${mark.id} must end after it starts and inside the domain`);
    }
  }
  if (spec.threshold) {
    if (spec.threshold.value < spec.domain.min || spec.threshold.value > spec.domain.max) throw new Error('range-number-line: the threshold lies outside the domain');
    if (!spec.bands.some((band) => band.to === spec.threshold!.value || band.from === spec.threshold!.value)) {
      throw new Error('range-number-line: the threshold must fall on a zone boundary');
    }
  }
  for (const entry of spec.companion?.entries ?? []) {
    if (entry.at < spec.domain.min || entry.at > spec.domain.max) throw new Error('range-number-line: a companion entry lies outside the domain');
    if (!entry.text.trim()) throw new Error('range-number-line: every companion entry needs text');
  }
  if (spec.focusMarkId && !markIds.has(spec.focusMarkId)) throw new Error('range-number-line: the focus mark does not exist');
  const required = metrics(spec).required;
  if (spec.viewport.height < required) throw new Error(`range-number-line: this scale needs at least ${Math.ceil(required)}px of height`);
}

function format(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 100) / 100);
}
